import * as React from 'react';

import { Button } from '@/components/ui/Button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Spinner } from '@/components/ui/Spinner';
import config from '@/config';
import { AuditLogInsertInput, CustomerInsertInput } from '@/generated/graphql';
import { execute } from '@/graphql/execute';
import { AuditLogAction, AuditLogEntityType } from '@/models/audit-log.models';
import { DEFAULT_UUID } from '@/models/constants';
import { createAuditLogInput } from '@/utils/audit-log.function';
import { useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { v4 as uuid } from 'uuid';
import {
  applyCustomerSecretConfigValues,
  buildCustomerSecretOperations,
  createDefaultCustomerSecretDrafts,
  createSecretUpsertDraft,
  CustomerSecretDrafts,
  customerSecretTargets,
  duplicateVlmProfileIds,
  manageableCustomerSecretTargets,
  stripCustomerSecretValues,
  withVlmProfileIds,
} from '../customerSecretStore';
import {
  createCustomerMutation,
  updateCustomerMutation,
  upsertCustomerS3BucketMutation,
} from '../graphql/mutation';
import {
  CustomerSecretSaveError,
  CustomerSecretSaveResult,
  saveCustomerSecretOperations,
} from '../saveCustomerSecrets';
import {
  applyVlmProfileSettings,
  invalidVlmProfileNames,
} from '../vlmGatewayProfileSettings';
import CustomerDetailsForm, {
  CustomerFormMode,
  CustomerFormValues,
} from './CustomerDetailsForm';
import { VlmSettingsReveal } from './VlmGatewayCard';

const fixedSecretTargetIds = new Set(
  customerSecretTargets.map((target) => target.id),
);

// Profile keys belong to profiles added in Form mode, which a mode switch clears.
const keepFixedSecretDrafts = (
  drafts: CustomerSecretDrafts,
): CustomerSecretDrafts =>
  Object.fromEntries(
    Object.entries(drafts).filter(([id]) => fixedSecretTargetIds.has(id)),
  );

interface AddCustomerDialogProps {
  open: boolean;
  onToggleOpen: () => void;
}

export function AddCustomerDialog({
  open,
  onToggleOpen,
}: AddCustomerDialogProps) {
  const navigate = useNavigate();
  const [formMode, setFormMode] = React.useState<CustomerFormMode>('form');
  const [isCreatingCustomer, setIsCreatingCustomer] = React.useState(false);
  const [secretDrafts, setSecretDrafts] = React.useState(
    createDefaultCustomerSecretDrafts(),
  );
  const [vlmSettingsReveal, setVlmSettingsReveal] =
    React.useState<VlmSettingsReveal>();
  const createS3BucketEnabled = config.customer.createS3BucketEnabled;

  const {
    register,
    control,
    reset,
    setError,
    setValue,
    getValues,
    formState: { isValid },
  } = useForm<CustomerFormValues>({
    mode: 'onChange',
    defaultValues: {
      name: '',
      storagePath: '',
      config: {},
      jsonConfig: {},
      vlmProfiles: {},
      vlmAddedProfileIds: {},
      vlmRemovedProfiles: [],
      vlmRenamedProfiles: {},
    },
  });

  React.useEffect(() => {
    if (formMode) {
      const values = getValues();
      reset({
        name: values.name,
        storagePath: values.storagePath,
        config: {},
        jsonConfig: {},
        vlmProfiles: {},
        vlmAddedProfileIds: {},
        vlmRemovedProfiles: [],
        vlmRenamedProfiles: {},
      });
      setSecretDrafts(keepFixedSecretDrafts);
    }
  }, [formMode, getValues, reset]);

  const { mutateAsync: createCustomer } = useMutation({
    mutationFn: (options: {
      input: CustomerInsertInput;
      auditLogInput: AuditLogInsertInput;
    }) => {
      return execute({
        query: createCustomerMutation,
        variables: {
          ...options,
        },
      });
    },
  });

  const handleSubmit = async () => {
    if (isCreatingCustomer) {
      return;
    }

    const formValues = getValues();
    // Profiles written in JSON mode get an id here.
    let config: unknown = withVlmProfileIds(formValues.jsonConfig);
    if (formMode === 'form') {
      const vlmProfiles = formValues.vlmProfiles ?? {};
      const invalidNames = invalidVlmProfileNames(vlmProfiles);
      if (invalidNames.length > 0) {
        setVlmSettingsReveal({ profileNames: invalidNames });
        toast.error(
          `Fix the VLM gateway profile settings for ${invalidNames.join(', ')} before creating the customer.`,
        );
        return;
      }
      config = applyVlmProfileSettings(
        { ...formValues.config },
        {},
        vlmProfiles,
        formValues.vlmAddedProfileIds ?? {},
      );
    }
    const duplicateIds = duplicateVlmProfileIds(config);
    if (duplicateIds.length > 0) {
      toast.error(
        `Not created: several VLM gateway profiles have the Id ${duplicateIds.join(', ')}, so they would share one API key. If one is a copy, remove its Id and ApiKey, then set its key in Form mode.`,
      );
      return;
    }
    // In Form mode every profile is new, so all of their keys can be set.
    const secretTargets =
      formMode === 'form'
        ? manageableCustomerSecretTargets(config)
        : customerSecretTargets;

    setIsCreatingCustomer(true);
    const timestamp = new Date().toISOString();

    const input: CustomerInsertInput = {
      name: formValues.name,
      config: stripCustomerSecretValues(config),
      id: uuid(),
      rowVersion: DEFAULT_UUID,
      createdAt: timestamp,
      updatedAt: timestamp,
    };

    try {
      const createResult = await createCustomer({
        input,
        auditLogInput: createAuditLogInput({
          action: AuditLogAction.CREATE_CUSTOMER,
          entities: [
            {
              id: input.id!,
              entity: AuditLogEntityType.CUSTOMER,
            },
          ],
        }),
      });

      const customerId = createResult.insertCustomerOne?.id;
      if (!customerId) {
        setIsCreatingCustomer(false);
        toast.error('Customer created, but unable to find the customer id.', {
          duration: 4000,
        });
        return;
      }

      let hasPartialFailure = false;

      const secretUpserts = buildCustomerSecretOperations(
        secretDrafts,
        secretTargets,
      );

      if (secretUpserts.length > 0) {
        try {
          let saved: CustomerSecretSaveResult;
          let secretError: CustomerSecretSaveError | undefined;
          try {
            saved = await saveCustomerSecretOperations(
              customerId,
              secretUpserts,
            );
          } catch (error) {
            if (!(error instanceof CustomerSecretSaveError)) {
              throw error;
            }
            // Still point config at the secrets senhub did create.
            saved = error;
            secretError = error;
          }

          // Created with secrets stripped; point the config at the new ones.
          if (Object.keys(saved.configValues).length > 0) {
            await execute({
              query: updateCustomerMutation,
              variables: {
                pkColumnInput: { id: customerId },
                setInput: {
                  config: applyCustomerSecretConfigValues(
                    input.config,
                    saved.configValues,
                    secretTargets,
                  ),
                  rowVersion: uuid(),
                },
                auditLogInput: createAuditLogInput({
                  action: AuditLogAction.UPDATE_CUSTOMER,
                  entities: [
                    {
                      id: customerId,
                      entity: AuditLogEntityType.CUSTOMER,
                    },
                  ],
                }),
              },
            });
          }

          if (saved.auditFailures.length > 0) {
            toast.warning(
              `Saved, but the audit log entry for ${saved.auditFailures
                .map((target) => target.label)
                .join(', ')} could not be written.`,
            );
          }

          if (secretError) {
            throw secretError;
          }
        } catch (error) {
          hasPartialFailure = true;
          toast.error('Customer created; secret update failed.', {
            duration: 4000,
            description: error instanceof Error ? error.message : undefined,
          });
        }
      }

      if (createS3BucketEnabled) {
        try {
          await execute({
            query: upsertCustomerS3BucketMutation,
            variables: {
              customerId,
              auditLogInput: createAuditLogInput({
                action: AuditLogAction.UPDATE_CUSTOMER,
                entities: [
                  {
                    id: customerId,
                    entity: AuditLogEntityType.CUSTOMER,
                  },
                ],
              }),
            },
          });
        } catch {
          hasPartialFailure = true;
          toast.error('Customer created, but S3 bucket creation failed.', {
            duration: 4000,
          });
        }
      }

      if (!hasPartialFailure) {
        toast.success('Customer created successfully!', {
          duration: 3000,
        });
      }
      await navigate(`/customers/${customerId}`);
    } catch {
      setIsCreatingCustomer(false);
      toast.error('Failed to create customer.', {
        duration: 4000,
      });
    }
  };

  const handleToggleOpenState = () => {
    if (isCreatingCustomer) {
      return;
    }

    onToggleOpen();
    // Profiles go with their staged keys, so a reopened dialog has neither.
    setValue('vlmProfiles', {});
    setValue('vlmAddedProfileIds', {});
    setValue('vlmRemovedProfiles', []);
    setValue('vlmRenamedProfiles', {});
    setSecretDrafts(createDefaultCustomerSecretDrafts());
    setVlmSettingsReveal(undefined);
  };

  return (
    <Dialog open={open} onOpenChange={handleToggleOpenState}>
      <DialogContent className="sm:max-w-[80vw] lg:max-w-[50vw]">
        <DialogHeader>
          <DialogTitle>Add Customer</DialogTitle>
          <DialogDescription></DialogDescription>
        </DialogHeader>

        <div className="grid max-h-[70vh] gap-6 overflow-auto px-1">
          <CustomerDetailsForm
            formMode={formMode}
            setFormMode={setFormMode}
            register={register}
            creatingCustomer={true}
            control={control}
            setError={setError}
            secretDrafts={secretDrafts}
            onStageSecretUpsert={(targetId, credentials) => {
              setSecretDrafts((t) => ({
                ...t,
                [targetId]: createSecretUpsertDraft(credentials),
              }));
            }}
            onStageSecretDelete={(targetId) => {
              setSecretDrafts((t) => ({
                ...t,
                [targetId]: { action: 'delete' },
              }));
            }}
            onDiscardSecretDraft={(targetId) => {
              setSecretDrafts((t) => {
                const { [targetId]: _discarded, ...rest } = t;
                return rest;
              });
            }}
            vlmSettingsReveal={vlmSettingsReveal}
          />
        </div>
        <DialogFooter>
          <Button
            onClick={() => void handleSubmit()}
            variant={'default'}
            className="md:w-[150px]"
            size={'sm'}
            disabled={!isValid || isCreatingCustomer}
          >
            Create
          </Button>
          <Button
            type="button"
            variant={'outline'}
            className="md:w-[150px]"
            size={'sm'}
            onClick={handleToggleOpenState}
            disabled={isCreatingCustomer}
          >
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
      <Dialog open={isCreatingCustomer} onOpenChange={() => {}}>
        <DialogContent
          className="sm:max-w-[420px] [&>button]:hidden"
          onInteractOutside={(event) => event.preventDefault()}
          onEscapeKeyDown={(event) => event.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle>Creating Customer</DialogTitle>
            <DialogDescription>
              {createS3BucketEnabled
                ? 'This may take a moment while we provision storage.'
                : 'This may take a moment while we finalize customer setup.'}
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-4">
            <Spinner className="h-10 w-10" />
            <span className="text-muted-foreground text-sm">
              {createS3BucketEnabled
                ? 'Creating customer and S3 bucket...'
                : 'Creating customer...'}
            </span>
          </div>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
